import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import { useEffect, useRef, useState } from "react";

import AllPagesSignature from "./AllPagesSignature";
import EachPageEachSignatory from "./EachPageEachSignatory";
import MultipleDocumentsSend from "./MultipleDocumentsSend";
import SignatoryNamesAtEnd from "./SignatoryNamesAtEnd";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const FileReaderPreview = ({
  file,
  signingMode,
  signatories = [],
  signatureFields,
  setSignatureFields,
  onPdfEdited,
  onSave,
  setSelectedField,
  fontSettings,
}) => {
  const [isEditable, setIsEditable] = useState(true);

  const containerRef = useRef(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [modePayload, setModePayload] = useState(null);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = "";
    const prevScrollTop = container.scrollTop;

    const renderPdfToHtml = async (pdfUrl, container) => {
      const loadingTask = pdfjsLib.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.3 });

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: context, viewport }).promise;

        const imgData = canvas.toDataURL();
        const img = document.createElement("img");
        img.src = imgData;
        img.style.width = "100%";
        img.style.display = "block";

        const pageContainer = document.createElement("div");
        pageContainer.classList.add("pdf-page-container");
        pageContainer.style.position = "relative";
        pageContainer.style.width = `${viewport.width}px`;
        pageContainer.style.height = `${viewport.height}px`;
        pageContainer.style.margin = "20px auto";
        pageContainer.style.border = "2px solid #e0e0e0";
        pageContainer.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
        pageContainer.style.background = "#fff";

        const textLayerDiv = document.createElement("div");
        textLayerDiv.style.position = "absolute";
        textLayerDiv.style.top = "0";
        textLayerDiv.style.left = "0";
        textLayerDiv.style.width = `${viewport.width}px`;
        textLayerDiv.style.height = `${viewport.height}px`;
        textLayerDiv.style.pointerEvents = isEditable ? "auto" : "none";

        signatureFields
          .filter((f) => f.pageIndex === i - 1)
          .forEach((fieldData, fieldIdx) => {
            const wrapper = document.createElement("div");
            wrapper.style.position = "absolute";
            wrapper.style.left = `${fieldData.x}px`;
            wrapper.style.top = `${fieldData.y}px`;
            wrapper.style.cursor = "move";
            wrapper.style.display = "inline-flex";
            wrapper.style.alignItems = "center";
            wrapper.style.gap = "2px";

            const nameDiv = document.createElement("div");
            nameDiv.innerText = fieldData.signatoryName;
            nameDiv.style.fontWeight = "bold";
            nameDiv.style.marginRight = "5px";

            const field = document.createElement("div");
            field.innerText = fieldData.label;
            field.contentEditable = true;
            field.style.padding = "4px 8px";
            field.style.border = "1px solid #000";
            field.style.backgroundColor = "#f9f9f9";
            field.style.borderRadius = "4px";
            field.style.fontSize = "14px";
            field.style.fontFamily = "Arial";
            field.style.color = "#000";

            let isDragging = false;
            let offsetX = 0;
            let offsetY = 0;
            let startX = 0;
            let startY = 0;

            const onMouseMove = (e) => {
              if (!isDragging) return;
              const parentRect = textLayerDiv.getBoundingClientRect();
              const newX = e.clientX - parentRect.left - offsetX;
              const newY = e.clientY - parentRect.top - offsetY;
              wrapper.style.left = `${newX}px`;
              wrapper.style.top = `${newY}px`;
              startX = newX;
              startY = newY;
            };

            const onMouseUp = () => {
              setSignatureFields((prev) =>
                prev.map((f) =>
                  f.id === fieldData.id ? { ...f, x: startX, y: startY } : f,
                ),
              );
              isDragging = false;
              document.removeEventListener("mousemove", onMouseMove);
              document.removeEventListener("mouseup", onMouseUp);
            };

            if (!fieldData.saved) {
              const deleteIcon = document.createElement("span");
              deleteIcon.innerHTML = "❌";
              deleteIcon.style.cursor = "pointer";
              deleteIcon.style.fontSize = "12px";
              deleteIcon.title = "Delete Field";
              deleteIcon.className = "field-icon";
              deleteIcon.addEventListener("click", () => {
                setSignatureFields((prev) =>
                  prev.filter((f) => f.id !== fieldData.id),
                );
              });

              const editIcon = document.createElement("span");
              editIcon.innerHTML = "✏️";
              editIcon.style.cursor = "pointer";
              editIcon.style.fontSize = "12px";
              editIcon.title = "Edit Field";
              editIcon.className = "field-icon";
              editIcon.addEventListener("click", () => setSelectedField(field));

              const saveIcon = document.createElement("span");
              saveIcon.innerHTML = "✅";
              saveIcon.style.cursor = "pointer";
              saveIcon.style.fontSize = "12px";
              saveIcon.style.userSelect = "none";
              saveIcon.title = "Save Position";
              saveIcon.className = "field-icon";
              saveIcon.addEventListener("click", () => {
                setSignatureFields((prev) =>
                  prev.map((f) =>
                    f.id === fieldData.id ? { ...f, saved: true } : f,
                  ),
                );
              });

              wrapper.appendChild(nameDiv);
              wrapper.appendChild(field);
              wrapper.appendChild(deleteIcon);
              wrapper.appendChild(editIcon);
              wrapper.appendChild(saveIcon);
            } else {
              wrapper.appendChild(nameDiv);
              wrapper.appendChild(field);
            }
            textLayerDiv.appendChild(wrapper);

            wrapper.addEventListener("mousedown", (e) => {
              if (!isEditable) return;
              if (e.target.classList.contains("field-icon")) return;
              isDragging = true;
              const wrapperRect = wrapper.getBoundingClientRect();
              offsetX = e.clientX - wrapperRect.left;
              offsetY = e.clientY - wrapperRect.top;
              startX = fieldData.x;
              startY = fieldData.y;
              document.addEventListener("mousemove", onMouseMove);
              document.addEventListener("mouseup", onMouseUp);
              e.stopPropagation();
            });
          });

        textLayerDiv.addEventListener("drop", (e) => {
          e.preventDefault();
          if (!isEditable) return;

          const label = e.dataTransfer.getData("text/plain");
          const rect = textLayerDiv.getBoundingClientRect();
          const dropX = e.clientX - rect.left;
          const dropY = e.clientY - rect.top;
          if (signingMode === "SIGNATURE_AT_END") {
            setModePayload({ type: "multi_doc", label, dropX, dropY });
            return;
          }
          if (signingMode === "SIGNATORY_FIELDS_AT_END") {
            setModePayload({ type: "same_doc_end", label, dropX, dropY });
            return;
          }
          if (signingMode === "ONE_PAGE_PER_SIGNATORY") {
            setModePayload({ type: "same_doc_pages", label, dropX, dropY });
            return;
          }
          if (signingMode === "SIGNATURE_ON_ALL_PAGES") {
            setModePayload({ type: "all_pages", label, dropX, dropY });
            return;
          }
          const wrapper = document.createElement("div");
          wrapper.style.position = "absolute";
          wrapper.style.left = `${dropX}px`;
          wrapper.style.top = `${dropY}px`;
          wrapper.style.cursor = "move";
          wrapper.style.display = "inline-flex";
          wrapper.style.alignItems = "center";
          wrapper.style.gap = "2px";

          const field = document.createElement("div");
          field.innerText = label;
          field.contentEditable = true;
          field.style.padding = "4px 8px";
          field.style.border = "1px solid #666";
          field.style.backgroundColor = "#f9f9f9";
          field.style.fontFamily = "Arial";
          field.style.fontSize = "14px";
          field.style.color = "#333";
          field.style.borderRadius = "4px";

          const deleteIcon = document.createElement("span");
          deleteIcon.innerHTML = "❌";
          deleteIcon.style.cursor = "pointer";
          deleteIcon.style.fontSize = "12px";
          deleteIcon.title = "Delete Field";
          deleteIcon.addEventListener("click", () => {
            wrapper.remove();
            setSignatureFields((prev) => prev.filter((f) => f.label !== label));
          });

          const editIcon = document.createElement("span");
          editIcon.innerHTML = "✏️";
          editIcon.style.cursor = "pointer";
          editIcon.style.fontSize = "12px";
          editIcon.title = "Edit Field";
          editIcon.addEventListener("click", () => setSelectedField(field));

          const saveIcon = document.createElement("span");
          saveIcon.innerHTML = "✅";
          saveIcon.style.cursor = "pointer";
          saveIcon.style.fontSize = "12px";
          saveIcon.style.userSelect = "none";
          saveIcon.title = "Save Position";

          wrapper.appendChild(field);
          wrapper.appendChild(deleteIcon);
          wrapper.appendChild(editIcon);
          wrapper.appendChild(saveIcon);
          textLayerDiv.appendChild(wrapper);

          const fieldData = {
            id: Date.now() + Math.random(),
            label,
            signatoryName: "",
            x: dropX,
            y: dropY,
            pageIndex: i - 1,
            saved: false,
            fontSettings: fontSettings,
          };
          setSignatureFields((prev) => [...prev, fieldData]);
        });
        textLayerDiv.addEventListener("dragover", (e) => e.preventDefault());
        pageContainer.appendChild(img);
        pageContainer.appendChild(textLayerDiv);
        container.appendChild(pageContainer);
      }
      setTimeout(() => {
        container.scrollTop = prevScrollTop;
      }, 0);
    };

    if (file?.type === "application/pdf" && fileUrl && container) {
      renderPdfToHtml(fileUrl, container);
    }
  }, [
    file,
    fileUrl,
    isEditable,
    setSelectedField,
    signingMode,
    signatories,
    //setSignatureFields,
    signatureFields,
  ]);

  const generateEditedPdfNormal = async () => {
    if (!file) return null;

    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const pages = pdfDoc.getPages();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

    signatureFields.forEach((field) => {
      if (!field.saved) return;

      const page = pages[field.pageIndex];
      if (!page) return;

      const { height } = page.getSize();

      const fontSize = 12;

      // ✅ FIXED Y calculation
      const pdfX = field.x;
      const pdfY = height - field.y - fontSize;

      const text = field.signatoryName
        ? `${field.signatoryName} ${field.label}`
        : field.label || "Signature";

      page.drawText(text, {
        x: pdfX,
        y: pdfY,
        size: fontSize,
        font,
        color: rgb(0, 0, 0),
      });
    });

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: "application/pdf" });
  };

  const generateEditedPdfAllPages = async () => {
    if (!file) return null;

    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const pages = pdfDoc.getPages();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    // 🔥 ADD THIS LINE HERE
    console.log("Total Fields:", signatureFields.length);
    console.log("All Fields:", signatureFields);
    const scale = 1.3; // 🔥 VERY IMPORTANT — same scale used in render

    signatureFields.forEach((field) => {
      if (!field.saved) return;

      if (
        field.pageIndex === undefined ||
        field.pageIndex === null ||
        field.pageIndex >= pages.length
      ) {
        return;
      }

      const page = pages[field.pageIndex];
      const { height } = page.getSize();

      const fontSize = 12;

      // ✅ Convert scaled DOM position back to real PDF size
      const pdfX = field.x / scale;
      const pdfY = height - field.y / scale - fontSize;

      const text = field.signatoryName
        ? `${field.signatoryName} ${field.label}`
        : field.label || "Signature";

      page.drawText(text, {
        x: pdfX,
        y: pdfY,
        size: fontSize,
        font,
        color: rgb(0, 0, 0),
      });
    });

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: "application/pdf" });
  };

  useEffect(() => {
    if (!signatureFields.length) return;

    if (onSave) onSave(signatureFields);

    const hasAllPagesField =
      signatureFields.length > 0 && signatureFields.every((f) => f.saved);

    const generate = hasAllPagesField
      ? generateEditedPdfAllPages
      : generateEditedPdfNormal;

    generate().then((blob) => {
      if (blob && onPdfEdited) onPdfEdited(blob);
    });
  }, [signatureFields]);

  return (
    <>
      <div
        ref={containerRef}
        style={{
          // overflowY: "auto",
          border: "1px solid #ccc",
          backgroundColor: "#fff",
          // padding: "10px",
          height: "600px",
        }}
      />

      {modePayload?.type === "multi_doc" && (
        <MultipleDocumentsSend
          containerRef={containerRef}
          label={modePayload.label}
          dropX={modePayload.dropX}
          dropY={modePayload.dropY}
          setSignatureFields={setSignatureFields}
          setSelectedField={setSelectedField}
          setModePayload={setModePayload}
        />
      )}

      {modePayload?.type === "same_doc_end" && (
        <SignatoryNamesAtEnd
          containerRef={containerRef}
          signatories={signatories}
          label={modePayload.label}
          dropX={modePayload.dropX}
          dropY={modePayload.dropY}
          setSignatureFields={setSignatureFields}
          setSelectedField={setSelectedField}
          setModePayload={setModePayload}
        />
      )}

      {modePayload?.type === "same_doc_pages" && (
        <EachPageEachSignatory
          containerRef={containerRef}
          signatories={signatories}
          label={modePayload.label}
          dropX={modePayload.dropX}
          dropY={modePayload.dropY}
          setSignatureFields={setSignatureFields}
          setSelectedField={setSelectedField}
          setModePayload={setModePayload}
        />
      )}

      {modePayload?.type === "all_pages" && (
        <AllPagesSignature
          containerRef={containerRef}
          label={modePayload.label}
          dropX={modePayload.dropX}
          dropY={modePayload.dropY}
          setSignatureFields={setSignatureFields}
          setModePayload={setModePayload}
        />
      )}
    </>
  );
};

export default FileReaderPreview;
